import { useSnackbar } from 'notistack';
import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';

import { useYupValidationResolver } from '~/hooks/useYupValidationResolver';
import { isValidOptionalPhoneNumber } from '~/utils/helper';

import { NewUserSendAccountDetails, sendUserAccountDetails } from '../../data';

export interface FormFields {
  username: string;
  password: string;
  sendSms: boolean;
  email: string;
  mobile: string;
}

export function useNewUserAccount(
  newAccountDetails: { fLoginUsername?: string; fLoginPassword?: string },
  employeeId: number,
  onClose: () => void,
) {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const validationSchema = yup.object().shape({
    username: yup.string().defined(),
    password: yup.string().defined(),
    sendSms: yup.boolean(),
    email: yup
      .string()
      .defined()
      .when('sendSms', {
        is: (sendSms: string) => !sendSms,
        then: yup
          .string()
          .required(t('laEmailRequired'))
          .email(t('laValidEmail')),
      }),
    mobile: yup
      .string()
      .defined()
      .when('sendSms', {
        is: true,
        then: yup
          .string()
          .required(t('laPhoneRequired'))
          .test(
            'phoneNumberValidation',
            t('phoneInvalid'),
            isValidOptionalPhoneNumber,
          ),
        otherwise: yup.string().notRequired(),
      }),
  });

  const resolver = useYupValidationResolver(
    validationSchema,
    'react-hook-form',
  );

  const form = useForm<FormFields>({
    resolver,
    defaultValues: {
      username: newAccountDetails?.fLoginUsername || '',
      password: newAccountDetails?.fLoginPassword || '',
      sendSms: false,
      email: '',
      mobile: '',
    },
  });

  const { handleSubmit } = form;

  const onSubmit = useMemo(() => {
    return handleSubmit((data) => {
      const payload: NewUserSendAccountDetails = {
        fLoginEmpId: employeeId,
        fLoginUsername: data.username,
        fLoginPassword: data.password,
      };
      if (data.sendSms) {
        payload.fSmsPhoneNumber = data.mobile;
      } else {
        payload.fAlternateEmail = data.email;
      }

      return sendUserAccountDetails(payload)
        .then(() => {
          enqueueSnackbar(t('laUsernPassSendSuc'), {
            variant: 'success',
          });
          onClose();
        })
        .catch(() => {
          enqueueSnackbar(t('laError'), {
            variant: 'error',
          });
        });
    });
  }, [handleSubmit, employeeId, enqueueSnackbar, onClose, t]);

  return { form, onSubmit };
}
